import { observer } from 'mobx-react-lite'
import { Box, Typography } from '@mui/material'
import { useLocalizationStore } from 'src/feature/localization/store/localization.store'
import { useNotificationStore } from 'src/feature/notification/store/notification.store'
import { ProjectBuilderAgent } from '../component/organisms/ProjectBuilderAgent'
import { useEffect, useState } from 'react'
import { ProjectBuilderMatchedUsers } from '../component/molecules/ProjectBuilderMatchedUsers'
import { ProjectBuilderResults } from '../component/organisms/ProjectBuilderResults'
import { useProjectBuilderStore } from '../store/project-builder.store'
import { Title1 } from 'src/feature/localization/component/atom/Title1'
import { AppBox } from 'src/component/common/analog/molecule/AppBox'

export const CreateProjectBuilderPage = observer(() => {
  const { translation } = useLocalizationStore()
  const projectBuilderStore = useProjectBuilderStore()
  const { enqueueError } = useNotificationStore()
  const projectBuilderTranslation = translation.pages.projectBuilder
  const [selectedSkill, setSelectedSkill] = useState<string | undefined>(undefined)
  const { isLoading } = projectBuilderStore.getGenericFieldsState('analyzeProjectRequirements')
  const { isLoading: isMappingSkills } = projectBuilderStore.getGenericFieldsState('mapSkillsToOrganisation')
  const hasMappedUsers = (projectBuilderStore.mappedSkills?.usersWithUsedSkills?.length ?? 0) > 0 || (projectBuilderStore.mappedSkills?.usersWithUnusedSkills?.length ?? 0) > 0
  const hasMappedGaps = (projectBuilderStore.mappedSkills?.gaps?.length ?? 0) > 0
  const shouldShowMatchedUsersColumn = Boolean(selectedSkill) && (isMappingSkills || hasMappedUsers || hasMappedGaps)

  useEffect(() => {
    projectBuilderStore.projectRequirements = undefined
    projectBuilderStore.mappedSkills = undefined
    setSelectedSkill(undefined)
  }, [projectBuilderStore, translation])

  const handleAnalyze = async (
    projectName: string,
    description: string,
    startDate: string,
    endDate: string,
  ) => {
    try {
      setSelectedSkill(undefined)

      await projectBuilderStore.analyzeProjectRequirements(projectName, description, startDate, endDate)

      if (projectBuilderStore.projectRequirements?.skills) {
        await projectBuilderStore.mapSkillsToOrganisation(projectBuilderStore.projectRequirements.skills)
      }
    } catch (error) {
      enqueueError(error instanceof Error ? error.message : projectBuilderTranslation.generateError())
    }
  }

  return (
    <Box sx={styles.root}>
      <Title1 m={0} textValue={projectBuilderTranslation.title} />
      <Typography variant='body2' sx={styles.subtitle}>{projectBuilderTranslation.subtitle()}</Typography>
      
      <AppBox sx={styles.container}>
        <Box sx={styles.leftColumn}>
          <ProjectBuilderAgent
            onAnalyze={handleAnalyze}
            isLoading={isLoading}
          />

          {projectBuilderStore.projectRequirements && (
            <ProjectBuilderResults
              projectRequirements={projectBuilderStore.projectRequirements}
              selectedSkill={selectedSkill}
              onSelectSkill={setSelectedSkill}
              isLoading={isMappingSkills}
            />
          )}
        </Box>
        
        {shouldShowMatchedUsersColumn && (
          <Box sx={styles.rightColumn}>
            <ProjectBuilderMatchedUsers
              usersWithUsedSkills={projectBuilderStore.mappedSkills?.usersWithUsedSkills ?? []}
              usersWithUnusedSkills={projectBuilderStore.mappedSkills?.usersWithUnusedSkills ?? []}
              gaps={projectBuilderStore.mappedSkills?.gaps ?? []}
              selectedSkill={selectedSkill}
            />
          </Box>
        )}
      </AppBox>
    </Box>
  )
})

const styles = {
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    margin: { xs: '1rem', md: '1rem 5rem' },
  },
  container: {
    display: 'grid',
    gridTemplateColumns: {
      xs: '1fr',
      md: '1fr 1fr',
    },
    gap: 20,
  },
  leftColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 20,
    minWidth: 0,
    overflow: 'hidden',
  },
  rightColumn: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    alignSelf: 'start',
    position: {
      xs: 'static',
      md: 'sticky',
    },
    top: {
      xs: 'auto',
      md: '1rem',
    },
    height: {
      xs: 'auto',
      md: 'calc(100dvh - 2rem)',
    },
  },
  subtitle: {
    color: 'text.secondary',
    marginTop: '-15px',
  },
}